import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { BaseExampleService, type BilingualExample } from '@/features/checking/domains/common/services/base-example-service';
import { HeaderServiceProxy } from '@/services/header-service-proxy';
import demoFile1Url from '@/assets/drafting/demo-files/一种自适应多级阻尼缓冲器.docx?url';
import demoFile2Url from '@/assets/drafting/demo-files/一种具有核壳结构的原位掺杂型高导热锂电池隔膜及其制备工艺.docx?url';
import demoFile3Url from '@/assets/drafting/demo-files/一种基于AI对话的全链路语音交易系统及实现方法.docx?url';

import demoFile4Url from '@/assets/drafting/demo-files/A Full-link Voice Transaction System and Implementation Method Based on AI Dialogue.docx?url';
import demoFile5Url from '@/assets/drafting/demo-files/An Adaptive Multi-stage Damping Buffer.docx?url';
import demoFile6Url from '@/assets/drafting/demo-files/An In-Situ Doped High Thermal Conductivity Lithium Battery Separator with Core-Shell Structure and Its Preparation Process.docx?url';

export const PatentDraftExampleService = Symbol('PatentDraftExampleService');
export interface PatentDraftExampleService extends BaseExampleService {}

@injectable()
export class PatentDraftExampleServiceImpl extends BaseExampleService implements PatentDraftExampleService {
    constructor(
        @inject(HeaderServiceProxy)
        private headerServiceProxy: HeaderServiceProxy
    ) {
        super();
    }

    siteRegion() {
        return this.headerServiceProxy?.headerService?.account?.siteRegion;
    }

    getExamples() {
        if (!this.siteRegion()) {
            return [];
        }

        const examples = {
            CN: [
                {
                    text: '一种自适应多级阻尼缓冲器.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: '20cdcbbe-aad5-4fc5-b159-cfd1b220c50e',
                        },
                        files: [
                            { url: demoFile1Url, name: '一种自适应多级阻尼缓冲器.docx' }
                        ],
                    },
                },
                {
                    text: '一种具有核壳结构的原位掺杂型高导热锂电池隔膜及其制备工艺.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: '9afecf7b-362d-467a-9dee-cb2df015c78a',
                        },
                        files: [
                            { url: demoFile2Url, name: '一种具有核壳结构的原位掺杂型高导热锂电池隔膜及其制备工艺.docx' }
                        ],
                    },
                },
                {
                    text: '一种基于AI对话的全链路语音交易系统及实现方法.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: '79d92dd7-16fc-4169-af5c-2be409b093aa',
                        },
                        files: [
                            { url: demoFile3Url, name: '一种基于AI对话的全链路语音交易系统及实现方法.docx' }
                        ],
                    },
                }
            ],
            US: [
                {
                    text: 'A Full-link Voice Transaction System and Implementation Method Based on AI Dialogue.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: '78c34181-c126-4354-93d9-d6e79b4b6c58',
                        },
                        files: [
                            { url: demoFile4Url, name: 'A Full-link Voice Transaction System and Implementation Method Based on AI Dialogue.docx' }
                        ],
                    },
                },
                {
                    text: 'An Adaptive Multi-stage Damping Buffer.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: '9d44a76e-2358-48e0-86e0-8ce0195555cc',
                        },
                        files: [
                            { url: demoFile5Url, name: 'An Adaptive Multi-stage Damping Buffer.docx' }
                        ],
                    },
                },
                {
                    text: 'An In-Situ Doped High Thermal Conductivity Lithium Battery Separator with Core-Shell Structure and Its Preparation Process.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: '08379dc5-dc1e-4786-abea-56600196d24e',
                        },
                        files: [
                            { url: demoFile6Url, name: 'An In-Situ Doped High Thermal Conductivity Lithium Battery Separator with Core-Shell Structure and Its Preparation Process.docx' }
                        ],
                    },
                }
            ],
            EU: [
                {
                    text: 'A Full-link Voice Transaction System and Implementation Method Based on AI Dialogue.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: '8a522869-c3d2-44d8-8ba7-f19192812ab4',
                        },
                        files: [
                            { url: demoFile4Url, name: 'A Full-link Voice Transaction System and Implementation Method Based on AI Dialogue.docx' }
                        ],
                    },
                },
                {
                    text: 'An Adaptive Multi-stage Damping Buffer.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: '1189b3fb-14f7-4674-802a-808bf280461f',
                        },
                        files: [
                            { url: demoFile5Url, name: 'An Adaptive Multi-stage Damping Buffer.docx' }
                        ],
                    },
                },
                {
                    text: 'An In-Situ Doped High Thermal Conductivity Lithium Battery Separator with Core-Shell Structure and Its Preparation Process.docx',
                    other: {
                        path: '/#/patent-drafting',
                        query: {
                            project_id: 'a9db91af-f51b-4427-a25d-073ad15ffefd',
                        },
                        files: [
                            { url: demoFile6Url, name: 'An In-Situ Doped High Thermal Conductivity Lithium Battery Separator with Core-Shell Structure and Its Preparation Process.docx' }
                        ],
                    },
                }
            ],
        }

        return examples[this.siteRegion()!] || [];
    }

    protected getExamplesData(): BilingualExample[] {
        return [];
    }
}
